import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatMoney } from '@/lib/money'

import { AdminHeader } from '../AdminLayout'
import { CollectionEditor, FormActions } from '../components/CollectionEditor'
import {
  I18nField,
  MoneyField,
  NumberField,
  SelectField,
  SwitchField,
  type I18nValue,
} from '../components/fields'
import { MediaField } from '../components/MediaField'
import { useAdminContext } from '../session'
import { useCollection } from '../useCollection'
import type { FoodCategory, FoodItem, FoodPack, I18n, Station } from '../types'

/**
 * La carte : catégories, plats, packs.
 *
 * C'est l'écran qui remplace la phrase « en arrière-plan je dois définir les
 * prix ». Rien n'y est écrit en dur : Poulet DG, Ndolé ou Eru sont des lignes
 * que l'équipe crée, renomme et retire.
 *
 * Les trois collections partagent la même mécanique — liste, tiroir,
 * suppression confirmée — fournie par `CollectionEditor`.
 */
export function AdminFoodPage() {
  const { t, i18n } = useTranslation()
  const { eventId, event } = useAdminContext()
  const currency = event?.currency ?? 'EUR'

  useEffect(() => {
    document.title = t('admin.section.food')
  }, [t])

  // Modifier un plat change la carte publique, et aussi les packs qui le
  // contiennent : leur composition affiche le nom du plat.
  const categories = useCollection<FoodCategory>({
    key: 'admin-food-categories',
    path: `/admin/events/${eventId}/food/categories`,
    eventId,
    related: [['menu'], ['admin-food-items']],
  })
  const items = useCollection<FoodItem>({
    key: 'admin-food-items',
    path: `/admin/events/${eventId}/food/items`,
    eventId,
    related: [['menu'], ['admin-food-packs']],
  })
  const packs = useCollection<FoodPack>({
    key: 'admin-food-packs',
    path: `/admin/events/${eventId}/food/packs`,
    eventId,
    related: [['menu']],
  })

  function label(value: I18n): string {
    return value[i18n.language] || value[Object.keys(value)[0] ?? ''] || '—'
  }

  function price(minor: number): string {
    return formatMoney({ amount_minor: minor, currency }, i18n.language)
  }

  const categoryName = (id: string | null) => {
    if (!id) return t('admin.food.noCategory')
    const found = categories.items.find((category) => category.id === id)
    return found ? label(found.name_i18n) : t('admin.food.noCategory')
  }

  return (
    <>
      <AdminHeader
        title={t('admin.section.food')}
        description={t('admin.food.description')}
      />

      <div className="grid gap-6">
        <CollectionEditor<FoodCategory>
          title={t('admin.food.categories')}
          description={t('admin.food.categoriesHint')}
          items={categories.items}
          busy={categories.busy}
          error={categories.error}
          emptyLabel={t('admin.food.noCategories')}
          createLabel={t('admin.food.addCategory')}
          onDelete={(category) => categories.remove.mutate({ id: category.id })}
          onReorder={(ids) => categories.reorder.mutate({ ids })}
          summarize={(category) => ({
            title: label(category.name_i18n),
            dimmed: !category.is_visible,
            badges: category.is_visible ? [] : [{ label: t('admin.hidden') }],
          })}
          renderForm={(category, close) => (
            <CategoryForm
              category={category}
              busy={categories.busy}
              onCancel={close}
              onSubmit={(payload) => {
                const done = { onSuccess: () => close() }
                if (category) categories.update.mutate({ id: category.id, ...payload }, done)
                else categories.create.mutate(payload, done)
              }}
            />
          )}
        />

        <CollectionEditor<FoodItem>
          title={t('admin.food.items')}
          description={t('admin.food.itemsHint')}
          items={items.items}
          busy={items.busy}
          error={items.error}
          emptyLabel={t('admin.food.noItems')}
          createLabel={t('admin.food.addItem')}
          onDelete={(item) => items.remove.mutate({ id: item.id })}
          onReorder={(ids) => items.reorder.mutate({ ids })}
          summarize={(item) => ({
            title: label(item.name_i18n),
            subtitle: categoryName(item.category_id),
            value: price(item.price_minor),
            dimmed: !item.is_active,
            badges: [
              ...(item.is_active ? [] : [{ label: t('admin.inactive') } as const]),
              ...(item.is_available
                ? []
                : [{ label: t('admin.food.unavailable'), tone: 'warning' } as const]),
              ...(item.stock_quantity === 0
                ? [{ label: t('admin.food.soldOut'), tone: 'danger' } as const]
                : []),
            ],
          })}
          renderForm={(item, close) => (
            <ItemForm
              item={item}
              categories={categories.items}
              categoryLabel={label}
              currency={currency}
              eventId={eventId}
              busy={items.busy}
              onCancel={close}
              onSubmit={(payload) => {
                const done = { onSuccess: () => close() }
                if (item) items.update.mutate({ id: item.id, ...payload }, done)
                else items.create.mutate(payload, done)
              }}
            />
          )}
        />

        <CollectionEditor<FoodPack>
          title={t('admin.food.packs')}
          description={t('admin.food.packsHint')}
          items={packs.items}
          busy={packs.busy}
          error={packs.error}
          emptyLabel={t('admin.food.noPacks')}
          createLabel={t('admin.food.addPack')}
          onDelete={(pack) => packs.remove.mutate({ id: pack.id })}
          onReorder={(ids) => packs.reorder.mutate({ ids })}
          summarize={(pack) => ({
            title: label(pack.name_i18n),
            subtitle: pack.items.map((line) => `${line.quantity}× ${line.name}`).join(' · '),
            value: price(pack.price_minor),
            dimmed: !pack.is_active,
            badges: pack.includes_admission
              ? [{ label: t('admin.food.withAdmission'), tone: 'success' } as const]
              : [],
          })}
          renderForm={(pack, close) => (
            <PackForm
              pack={pack}
              items={items.items}
              itemLabel={label}
              currency={currency}
              eventId={eventId}
              busy={packs.busy}
              onCancel={close}
              onSubmit={(payload) => {
                const done = { onSuccess: () => close() }
                if (pack) packs.update.mutate({ id: pack.id, ...payload }, done)
                else packs.create.mutate(payload, done)
              }}
            />
          )}
        />
      </div>
    </>
  )
}

// ── Formulaires ────────────────────────────────────────────────────

function CategoryForm({
  category,
  busy,
  onSubmit,
  onCancel,
}: {
  category: FoodCategory | null
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState<I18nValue>(category?.name_i18n ?? {})
  const [description, setDescription] = useState<I18nValue>(category?.description_i18n ?? {})
  const [visible, setVisible] = useState(category?.is_visible ?? true)

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        <I18nField label={t('admin.food.name')} value={name} onChange={setName} />
        <I18nField
          label={t('admin.food.descriptionField')}
          value={description}
          onChange={setDescription}
        />
        <SwitchField
          label={t('admin.food.visible')}
          hint={t('admin.food.visibleHint')}
          value={visible}
          onChange={setVisible}
        />
      </div>
      <FormActions
        busy={busy}
        onCancel={onCancel}
        onSubmit={() =>
          onSubmit({
            name_i18n: name,
            description_i18n: description,
            is_visible: visible,
          })
        }
      />
    </div>
  )
}

function ItemForm({
  item,
  categories,
  categoryLabel,
  currency,
  eventId,
  busy,
  onSubmit,
  onCancel,
}: {
  item: FoodItem | null
  categories: FoodCategory[]
  categoryLabel: (value: I18n) => string
  currency: string
  eventId: string | undefined
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState<I18nValue>(item?.name_i18n ?? {})
  const [description, setDescription] = useState<I18nValue>(item?.description_i18n ?? {})
  const [categoryId, setCategoryId] = useState(item?.category_id ?? '')
  const [priceMinor, setPriceMinor] = useState<number | null>(item?.price_minor ?? null)
  const [unitCostMinor, setUnitCostMinor] = useState<number | null>(
    item?.unit_cost_minor ?? null,
  )
  const [station, setStation] = useState<Station>(item?.station ?? 'kitchen')
  const [stock, setStock] = useState<number | null>(item?.stock_quantity ?? null)
  const [maxPerOrder, setMaxPerOrder] = useState<number | null>(item?.max_per_order ?? null)
  const [available, setAvailable] = useState(item?.is_available ?? true)
  const [active, setActive] = useState(item?.is_active ?? true)
  const [mediaId, setMediaId] = useState<string | null>(item?.media_id ?? null)

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        <I18nField label={t('admin.food.name')} value={name} onChange={setName} />
        <I18nField
          label={t('admin.food.descriptionField')}
          value={description}
          onChange={setDescription}
        />
        <SelectField
          label={t('admin.food.category')}
          value={categoryId}
          onChange={setCategoryId}
          options={[
            { value: '', label: t('admin.food.noCategory') },
            ...categories.map((category) => ({
              value: category.id,
              label: categoryLabel(category.name_i18n),
            })),
          ]}
        />
        <MoneyField
          label={t('admin.food.price')}
          minor={priceMinor}
          currency={currency}
          onChange={setPriceMinor}
        />
        {/* Ce que l'unité coûte, par opposition à ce qu'elle rapporte.
            Facultatif : laissé vide, le produit sort de la valeur de la
            réserve au lieu d'y compter pour zéro. */}
        <MoneyField
          label={t('admin.food.unitCost')}
          hint={t('admin.food.unitCostHint')}
          minor={unitCostMinor}
          currency={currency}
          onChange={setUnitCostMinor}
        />
        <SelectField<Station>
          label={t('admin.food.station')}
          hint={t('admin.food.stationHint')}
          value={station}
          onChange={setStation}
          options={[
            { value: 'kitchen', label: t('admin.food.kitchen') },
            { value: 'bar', label: t('admin.food.bar') },
            { value: 'none', label: t('admin.food.noStation') },
          ]}
        />
        <NumberField
          label={t('admin.food.stock')}
          hint={t('admin.food.stockHint')}
          value={stock}
          min={0}
          onChange={setStock}
        />
        <NumberField
          label={t('admin.food.maxPerOrder')}
          hint={t('admin.food.maxPerOrderHint')}
          value={maxPerOrder}
          min={1}
          max={99}
          onChange={setMaxPerOrder}
        />
        <SwitchField
          label={t('admin.food.available')}
          hint={t('admin.food.availableHint')}
          value={available}
          onChange={setAvailable}
        />
        <SwitchField
          label={t('admin.active')}
          hint={t('admin.activeHint')}
          value={active}
          onChange={setActive}
        />
        <div className="sm:col-span-2">
          <MediaField
            label={t('admin.media.photo')}
            hint={t('admin.media.photoHint')}
            value={mediaId}
            eventId={eventId}
            onChange={setMediaId}
          />
        </div>
      </div>
      <FormActions
        busy={busy}
        onCancel={onCancel}
        onSubmit={() =>
          onSubmit({
            name_i18n: name,
            description_i18n: description,
            media_id: mediaId,
            // Chaîne vide = « aucune catégorie » : le serveur attend `null`.
            category_id: categoryId || null,
            price_minor: priceMinor ?? 0,
            unit_cost_minor: unitCostMinor,
            station,
            stock_quantity: stock,
            max_per_order: maxPerOrder,
            is_available: available,
            is_active: active,
          })
        }
      />
    </div>
  )
}

function PackForm({
  pack,
  items,
  itemLabel,
  currency,
  eventId,
  busy,
  onSubmit,
  onCancel,
}: {
  pack: FoodPack | null
  items: FoodItem[]
  itemLabel: (value: I18n) => string
  currency: string
  eventId: string | undefined
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState<I18nValue>(pack?.name_i18n ?? {})
  const [description, setDescription] = useState<I18nValue>(pack?.description_i18n ?? {})
  const [priceMinor, setPriceMinor] = useState<number | null>(pack?.price_minor ?? null)
  const [unitCostMinor, setUnitCostMinor] = useState<number | null>(
    pack?.unit_cost_minor ?? null,
  )
  const [admission, setAdmission] = useState(pack?.includes_admission ?? false)
  const [active, setActive] = useState(pack?.is_active ?? true)
  const [mediaId, setMediaId] = useState<string | null>(pack?.media_id ?? null)
  // Le stock et le plafond par commande existaient côté serveur sans champ
  // pour les régler : un pack ne pouvait être limité qu'en base.
  const [stock, setStock] = useState<number | null>(pack?.stock_quantity ?? null)
  const [maxPerOrder, setMaxPerOrder] = useState<number | null>(pack?.max_per_order ?? null)
  // `food_item_id` → quantité. Un pack est composé de plats EXISTANTS : on ne
  // duplique jamais le catalogue dans un pack.
  const [composition, setComposition] = useState<Record<string, number>>(() =>
    Object.fromEntries((pack?.items ?? []).map((line) => [line.food_item_id, line.quantity])),
  )

  const chosen = Object.entries(composition).filter(([, quantity]) => quantity > 0)

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        <I18nField label={t('admin.food.name')} value={name} onChange={setName} />
        <I18nField
          label={t('admin.food.descriptionField')}
          value={description}
          onChange={setDescription}
        />
        <MoneyField
          label={t('admin.food.price')}
          minor={priceMinor}
          currency={currency}
          onChange={setPriceMinor}
        />
        {/* Ce que l'unité coûte, par opposition à ce qu'elle rapporte.
            Facultatif : laissé vide, le produit sort de la valeur de la
            réserve au lieu d'y compter pour zéro. */}
        <MoneyField
          label={t('admin.food.unitCost')}
          hint={t('admin.food.unitCostHint')}
          minor={unitCostMinor}
          currency={currency}
          onChange={setUnitCostMinor}
        />
        <NumberField
          label={t('admin.food.stock')}
          hint={t('admin.food.stockHint')}
          value={stock}
          min={pack?.sold_count ?? 0}
          onChange={setStock}
        />
        <NumberField
          label={t('admin.food.maxPerOrder')}
          hint={t('admin.food.maxPerOrderHint')}
          value={maxPerOrder}
          min={1}
          max={99}
          onChange={setMaxPerOrder}
        />
        <SwitchField
          label={t('admin.food.includesAdmission')}
          hint={t('admin.food.includesAdmissionHint')}
          value={admission}
          onChange={setAdmission}
        />
        <SwitchField
          label={t('admin.active')}
          hint={t('admin.activeHint')}
          value={active}
          onChange={setActive}
        />
        <div className="sm:col-span-2">
          <MediaField
            label={t('admin.media.photo')}
            value={mediaId}
            eventId={eventId}
            onChange={setMediaId}
          />
        </div>
      </div>

      <div className="mt-5">
        <p className="text-caption uppercase tracking-wide text-content-muted">
          {t('admin.food.composition')}
        </p>
        <p className="mt-1 text-xs text-content-muted">{t('admin.food.compositionHint')}</p>

        <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex min-w-0 items-center justify-between gap-4 rounded-md border border-line-subtle px-3 py-2"
            >
              <span className="min-w-0 truncate text-sm text-content-primary">
                {itemLabel(item.name_i18n)}
              </span>
              <input
                type="number"
                min={0}
                max={20}
                value={composition[item.id] ?? 0}
                onChange={(event) =>
                  setComposition((current) => ({
                    ...current,
                    [item.id]: Number(event.target.value),
                  }))
                }
                className="w-16 shrink-0 rounded-md border border-line bg-bg-base px-2 py-1 text-right text-sm tabular-nums text-content-primary"
              />
            </li>
          ))}
        </ul>
      </div>

      {chosen.length === 0 ? (
        <p className="mt-3 text-xs text-state-warning">{t('admin.food.packNeedsItems')}</p>
      ) : null}

      <FormActions
        busy={busy || chosen.length === 0}
        onCancel={onCancel}
        onSubmit={() =>
          onSubmit({
            name_i18n: name,
            description_i18n: description,
            media_id: mediaId,
            price_minor: priceMinor ?? 0,
            unit_cost_minor: unitCostMinor,
            stock_quantity: stock,
            max_per_order: maxPerOrder,
            includes_admission: admission,
            is_active: active,
            items: chosen.map(([food_item_id, quantity]) => ({

              food_item_id,
              quantity,
              is_substitutable: false,
            })),
          })
        }
      />
    </div>
  )
}
